import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { handleErrorMessage } from "../../../../helpers/methods";
import {
  AstroBasemapOption,
  AstroFocusCountry,
  AstroReportFilters,
  AstroReportScope,
} from "../../../../models/interfaces";
import { getAstrocartographyFocusOptions } from "../TimeAnalysisService";
import astrocartographyFilterDialogStyles from "./AstrocartographyFilterDialog.styles";

interface AstrocartographyFilterDialogProps {
  open: boolean;
  onClose: () => void;
  /** Receives only the filters the user actually set. */
  onDownload: (filters: AstroReportFilters) => void;
  /** Name of the chosen profile, shown so the dialog has context. */
  profileName?: string;
  isDownloading: boolean;
}

const SCOPES: AstroReportScope[] = ["world", "continent", "country"];

const SCOPE_LABEL_KEYS: Record<AstroReportScope, string> = {
  world: "astroRegionWorld",
  continent: "astroRegionContinent",
  country: "astroRegionCountry",
};

/**
 * Collects the optional region and map-style filters before the report job is
 * started. Everything here is optional: leaving it untouched sends no filters
 * at all, which is the backend's whole-world, vector-map default.
 */
const AstrocartographyFilterDialog = (
  props: AstrocartographyFilterDialogProps,
) => {
  const classes = astrocartographyFilterDialogStyles;
  const { t } = useTranslation();
  const { open, onClose, onDownload, profileName, isDownloading } = props;

  const [isOptionsLoading, setIsOptionsLoading] = useState<boolean>(false);
  const [continents, setContinents] = useState<string[]>([]);
  const [countries, setCountries] = useState<AstroFocusCountry[]>([]);
  const [basemaps, setBasemaps] = useState<AstroBasemapOption[]>([]);

  const [scope, setScope] = useState<AstroReportScope>("world");
  const [continent, setContinent] = useState<string>("");
  const [country, setCountry] = useState<AstroFocusCountry | null>(null);
  const [basemap, setBasemap] = useState<string>("");

  /** The map style the backend uses when `basemap` is left out of the body. */
  const defaultBasemapId = useMemo(
    () => basemaps.find((option) => option.default)?.id ?? "",
    [basemaps],
  );

  /* ------------------------------ focus options ----------------------------- */

  /** Keeps a reopen during the first request from firing a second one. */
  const isLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!open || isLoadingRef.current) return;
    if (basemaps.length || continents.length) return;

    const loadOptions = async () => {
      try {
        isLoadingRef.current = true;
        setIsOptionsLoading(true);
        const response = await getAstrocartographyFocusOptions();
        setContinents(response?.continents || []);
        setCountries(response?.countries || []);
        setBasemaps(response?.basemaps || []);
        setBasemap(
          (response?.basemaps || []).find((option: AstroBasemapOption) =>
            option.default,
          )?.id || "",
        );
      } catch (error: any) {
        // The dialog still works without the lists — a world report needs none.
        handleErrorMessage(error);
      } finally {
        isLoadingRef.current = false;
        setIsOptionsLoading(false);
      }
    };
    loadOptions();
  }, [open, basemaps.length, continents.length]);

  /* --------------------------------- state --------------------------------- */

  const handleScopeChange = (value: AstroReportScope | null) => {
    if (!value) return;
    setScope(value);
    // Drop the selection that no longer applies, so a stale continent can
    // never be sent with a country report.
    if (value !== "continent") setContinent("");
    if (value !== "country") setCountry(null);
  };

  const buildFilters = (): AstroReportFilters => {
    const filters: AstroReportFilters = {};
    if (scope === "continent" && continent) filters.continent = continent;
    if (scope === "country" && country) filters.country = country.name;
    // The default map style is the backend's own, so it stays out of the body.
    if (basemap && basemap !== defaultBasemapId) filters.basemap = basemap;
    return filters;
  };

  const isRegionIncomplete =
    (scope === "continent" && !continent) || (scope === "country" && !country);

  const selectedBasemap = basemaps.find((option) => option.id === basemap);

  const getSummary = () => {
    if (scope === "continent") {
      return continent
        ? t("astroSummaryContinent", { region: continent })
        : t("astroSummaryPickContinent");
    }
    if (scope === "country") {
      return country
        ? t("astroSummaryCountry", { region: country.name })
        : t("astroSummaryPickCountry");
    }
    return t("astroSummaryWorld");
  };

  /* --------------------------------- render -------------------------------- */

  const getRegionField = () => {
    if (scope === "world") return null;

    if (scope === "continent") {
      return (
        <Autocomplete
          sx={classes.field}
          options={continents}
          value={continent || null}
          disabled={isOptionsLoading || !continents.length}
          onChange={(event, value) => setContinent(value || "")}
          componentsProps={{ paper: { sx: classes.popperPaper } }}
          renderInput={(params) => (
            <TextField {...params} placeholder={t("astroSelectContinent")} />
          )}
        />
      );
    }

    return (
      <>
        <Autocomplete
          sx={classes.field}
          options={countries}
          value={country}
          disabled={isOptionsLoading || !countries.length}
          onChange={(event, value) => setCountry(value)}
          getOptionLabel={(option: AstroFocusCountry) => option.name}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          // Grouping keeps ~129 entries navigable without a second dropdown.
          groupBy={(option: AstroFocusCountry) => option.continent}
          componentsProps={{ paper: { sx: classes.popperPaper } }}
          renderOption={(optionProps, option: AstroFocusCountry) => (
            <li {...optionProps} key={option.name}>
              <Box sx={classes.optionRow}>
                <Box component="span">{option.name}</Box>
                {option.has_map && (
                  <Box component="span" sx={classes.optionHint}>
                    {t("astroCountryHasMap")}
                  </Box>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder={t("astroSelectCountry")} />
          )}
        />
        {country && !country.has_map && (
          <Typography sx={classes.hint} component="div">
            <InfoOutlinedIcon />
            {t("astroCountryNoMapHint")}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isDownloading ? undefined : onClose}
      fullWidth
      PaperProps={{ sx: classes.paper }}
      BackdropProps={{ sx: classes.backdrop }}
    >
      <Box sx={classes.header}>
        <Box>
          <Typography sx={classes.title}>
            {t("astroReportOptionsTitle")}
          </Typography>
          <Typography sx={classes.subtitle}>
            {profileName
              ? t("astroReportOptionsSubtitleFor", { name: profileName })
              : t("astroReportOptionsSubtitle")}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={isDownloading}
          sx={classes.closeButton}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={classes.content}>
        {isOptionsLoading ? (
          <Box sx={classes.loadingRow}>
            <CircularProgress size={22} sx={{ color: "#9053F6" }} />
            {t("astroLoadingOptions")}
          </Box>
        ) : (
          <>
            <Box sx={classes.section}>
              <Box sx={classes.sectionLabelRow}>
                <PublicOutlinedIcon sx={classes.sectionIcon} />
                <Typography sx={classes.sectionLabel}>
                  {t("astroReportRegion")}
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                value={scope}
                onChange={(event, value) => handleScopeChange(value)}
                sx={classes.scopeGroup}
              >
                {SCOPES.map((value) => (
                  <ToggleButton key={value} value={value}>
                    {t(SCOPE_LABEL_KEYS[value])}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {getRegionField()}
            </Box>

            <Box sx={classes.section}>
              <Box sx={classes.sectionLabelRow}>
                <LayersOutlinedIcon sx={classes.sectionIcon} />
                <Typography sx={classes.sectionLabel}>
                  {t("astroMapStyle")}
                </Typography>
              </Box>
              <Select
                sx={classes.selectField}
                value={basemaps.length ? basemap : ""}
                displayEmpty
                disabled={!basemaps.length}
                onChange={(event) => setBasemap(event.target.value as string)}
                MenuProps={{ PaperProps: { sx: classes.popperPaper } }}
              >
                {!basemaps.length && (
                  <MenuItem value="">{t("astroNoMapStyles")}</MenuItem>
                )}
                {basemaps.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {selectedBasemap?.network && (
                <Typography sx={classes.hint} component="div">
                  <InfoOutlinedIcon />
                  {t("astroMapStyleNetworkHint")}
                </Typography>
              )}
            </Box>

            <Box sx={classes.summary}>
              <InfoOutlinedIcon sx={{ fontSize: "16px", mt: "1px" }} />
              <Box component="span">{getSummary()}</Box>
            </Box>
          </>
        )}
      </Box>

      <Box sx={classes.actions}>
        <Button
          onClick={onClose}
          disabled={isDownloading}
          sx={classes.cancelButton}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={() => onDownload(buildFilters())}
          disabled={isOptionsLoading || isRegionIncomplete || isDownloading}
          startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
          sx={classes.downloadButton}
        >
          {t("astroDownloadReport")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AstrocartographyFilterDialog;
